import React, { useState } from "react";
import { Button, Container, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import "./styles.css";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../../config";
import { IconButton, InputAdornment } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { saveUserCredentials, getStoredCredentials, loadImageHashesFromBackend, validateOfflinePassword } from "../../db";

const Login = ({ setUserLoggedIn }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const handleUsernameChange = (e) => {
    setUsername(e.target.value);
    if (loginError) setLoginError(''); // Clear error when user starts typing
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (loginError) setLoginError(''); // Clear error when user starts typing
  };

  // Smart login handler that automatically detects online/offline
  const handleSmartLogin = async () => {
    const errs = {};
    if (!username) errs.username = "Username is required!";
    if (!password) errs.password = "Password is required!";
    setErrors(errs);
    setLoginError(''); // Clear previous login errors
    if (Object.keys(errs).length) return;

    setIsLoggingIn(true);

    try {
      // Always try online login first, regardless of navigator.onLine status
      // This ensures we attempt backend login even if browser reports offline
      try {
        const data = { username, password };
        const response = await axios.post(`${config.API_BASE_URL}/api/auth/login/`, data, {
          timeout: 10000 // 10 second timeout
        });

        if (response.data.token) {
          const userProfile = JSON.stringify(response.data.user_profile);
          localStorage.setItem("user_profile", userProfile);
          localStorage.setItem("optha-token", response.data.token);
          
          const optometristRole = response.data.user_profile?.optometrist_role;
          if (optometristRole) {
            localStorage.setItem("optometrist_role", optometristRole);
          }
          
          // Save credentials for offline access
          await saveUserCredentials(username, password, response.data.token, response.data.user_profile, optometristRole);
          
          // Load image hashes from backend for duplicate detection
          await loadImageHashesFromBackend();
          
          setUserLoggedIn(true);
          
          // Role-based navigation
          if (optometristRole === "Ophthalmologist") {
            navigate("/patientrecord");
          } else {
            navigate("/splashscreen");
          }
          return;
        }
      } catch (onlineError) {
        console.error('Online login failed:', onlineError);
        
        // Try offline login if we have stored credentials and we're offline
        // Check for network errors, server unavailable (503), timeout, or no response
        const isOfflineError = onlineError.code === 'NETWORK_ERROR' || 
                              onlineError.code === 'ECONNABORTED' || // Axios timeout error
                              onlineError.message === 'Network Error' || 
                              onlineError.message === 'Offline: API call blocked' ||
                              onlineError.message?.includes('timeout') ||
                              !onlineError.response ||
                              (onlineError.response && onlineError.response.status === 503);
        
        if (isOfflineError) {
          // Only try offline login if we have stored credentials
          const hasOfflineCredentials = await getStoredCredentials();
          if (hasOfflineCredentials) {
            await tryOfflineLogin();
          } else {
            setLoginError("Unable to connect to server. Please check your internet connection and try again.");
          }
        } else {
          // Server error - show the actual error
          if (onlineError.response) {
            const errorMessage = onlineError.response.data?.message || 
                               onlineError.response.data?.error || 
                               onlineError.response.data?.detail ||
                               `Server error: ${onlineError.response.status}`;
            setLoginError(errorMessage);
          } else {
            setLoginError("Login failed. Please check your credentials and try again.");
          }
        }
        return;
      }
    } catch (error) {
      console.error('Login failed:', error);
      setLoginError("Login failed. Please check your credentials and internet connection.");
    } finally {
      setIsLoggingIn(false);
    }
  };

  // Offline login helper
  const tryOfflineLogin = async () => {
    try {
      const credentials = await getStoredCredentials();
      if (!credentials) {
        setLoginError("No offline credentials found. Please login online first when you have internet connection.");
        return;
      }

      // Validate both username and password
      const isValidCredentials = await validateOfflinePassword(username, password);
      if (!isValidCredentials) {
        setLoginError("Invalid username or password. Please use the same credentials you used for online login.");
        return;
      }

      // Set credentials in localStorage
      localStorage.setItem("user_profile", credentials.user_profile);
      localStorage.setItem("optha-token", credentials.token);
      localStorage.setItem("optometrist_role", credentials.optometrist_role);
      
      // Note: Image hashes are already loaded from previous online login
      // No need to reload them during offline login
      
      setUserLoggedIn(true);
      
      // Role-based navigation
      if (credentials.optometrist_role === "Ophthalmologist") {
        navigate("/patientrecord");
      } else {
        navigate("/splashscreen");
      }
    } catch (error) {
      console.error('Offline login failed:', error);
      setLoginError("Offline login failed. Please try again or login online when you have internet connection.");
    }
  };


  return (
    <div style={{ height: isMobile ? "auto" : "90%", display: "flex", justifyContent: "center", alignItems: "center" }}>
      <Container
        className={`container ${isMobile ? 'container-mobile' : ''}`}
        sx={{
          width: { xs: "90vw", md: "70vw" },
          height: { xs: "auto", md: "52vh" },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          border: "1px solid var(--box-stroke, rgba(0, 0, 0, 0.05))",
          borderRadius: "4px",
          padding: "24px"
        }}
      >
        <div className={`login-image ${isMobile ? 'login-image-mobile' : ''}`}>
          <img src="/assests/images/118979-health-blue.jpg" style={{ width: "100%", height: "100%" }} alt="Login" />
        </div>
        <div className="login-form">
          <div style={{ width: "100%", height: "75%" }}>
            <div style={{ padding: "16px" }}>
              <Typography color="var(--dark-gray, #181818)" fontSize="2.25vh" fontWeight="600" lineHeight="137%">Log into your Account</Typography>
            </div>
            <div style={{ padding: "16px", display: "flex", flexDirection: "column", gap: "16px" }}>
              <div className={`input-group ${isMobile ? 'input-group-mobile' : ''}`}>
                <Typography color="var(--black, #404040)" fontSize="1.75vh" fontWeight="500" lineHeight="110%">User ID </Typography>
                <TextField
                  sx={{ width: { xs: "100%", md: "22vw" } }}
                  error={!!errors.username}
                  helperText={errors.username}
                  placeholder="Enter User ID"
                  value={username}
                  onChange={handleUsernameChange}
                />
              </div>
              <div className={`input-group ${isMobile ? 'input-group-mobile' : ''}`}>
                <Typography color="var(--black, #404040)" fontSize="1.75vh" fontWeight="500" lineHeight="110%">Password</Typography>

                <TextField
                  sx={{
                    width: { xs: "100%", md: "22vw" },
                    backgroundColor: password ? "#E8F0FE" : "#FFFFFF", // conditional background
                    borderRadius: "4px",
                  }}
                  type={showPassword ? "text" : "password"}
                  error={!!errors.password}
                  helperText={errors.password}
                  placeholder="Enter your Password"
                  value={password}
                  onChange={handlePasswordChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{
                            backgroundColor: password ? "#E8F0FE" : "#FFFFFF", // same conditional color
                            borderRadius: "4px",
                            padding: "4px",
                          }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />


              </div>
              {/* Login Error Display */}
              {loginError && (
                <div style={{
                  padding: "12px",
                  backgroundColor: "#fee",
                  border: "1px solid #fcc",
                  borderRadius: "4px",
                  color: "#c33",
                  fontSize: "14px",
                  textAlign: "center"
                }}>
                  {loginError}
                </div>
              )}
              
              <div className={`button-group ${isMobile ? 'button-group-mobile' : ''}`}>
                <Button
                  variant="primary"
                  sx={{
                    background: username && password ? "var(--gradient, linear-gradient(270deg, #051572 0%, #0820B4 100%))" : "#b1b1b1",
                    boxShadow: "0px 2px 4px 0px rgba(0, 0, 0, 0.08), 0px 0px 6px 0px rgba(0, 0, 0, 0.02)",
                    color: username && password ? "var(--white, #FFF)" : "gray",
                    borderRadius: "8px"
                  }}
                  onClick={handleSmartLogin}
                  disabled={!username || !password || isLoggingIn}
                >
                  {isLoggingIn ? "Logging in..." : "Login"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default Login;
